/* eslint-disable @typescript-eslint/no-shadow */
import { useMutation } from '@tanstack/react-query';

import TQConnectServiceRequestAPI from '../../../api/TQConnectServiceRequestAPI';
import showAppError from '../../../shared/error';
import { queryClient } from '../../../shared/queryClient';
import {
  ITQCreateServiceRequestBody,
  ITQCreateServiceRequestResponseBody,
  ITQCreateServiceRequestResponseResult,
  ITQCreateTagBody,
  ITQCreateTagResult,
  ITQServiceRequest,
  ITQUpdateServiceRequestBody,
  ITQUpdateServiceRequestResponseBody,
  ITQUpdateServiceRequestResponseResult,
  ITQUpdateServiceRequestResult,
} from '../../../types/tqConnectTypes/service-requests';

/**
 * Custom hook to create a TQ Connect service request
 *
 * @param onSuccessCallback - Optional callback function to execute on successful creation
 * @returns useMutation result for creating service request
 */
export const useCreateTQServiceRequest = (
  onSuccessCallback?: (data: ITQServiceRequest) => void,
) => {
  const { mutate: createServiceRequest, ...values } = useMutation({
    mutationFn: (data: ITQCreateServiceRequestBody) => {
      return TQConnectServiceRequestAPI.createServiceRequest(data);
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 201 || resp?.data?.code === 200) {
        // Invalidate all service requests related queries
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS' ||
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS_STATISTICS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback(resp.data.result);
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { createServiceRequest, ...values };
};

/**
 * Custom hook to update a TQ Connect service request
 *
 * @param serviceRequestId - The ID of the service request to update
 * @param onSuccessCallback - Optional callback function to execute on successful update
 * @returns useMutation result for updating service request
 */
export const useUpdateTQServiceRequest = (
  serviceRequestId: string,
  onSuccessCallback?: (data: ITQUpdateServiceRequestResult) => void,
) => {
  const { mutate: updateServiceRequest, ...values } = useMutation({
    mutationFn: ({
      serviceRequestId,
      data,
    }: {
      serviceRequestId: string;
      data: ITQUpdateServiceRequestBody;
    }) => {
      return TQConnectServiceRequestAPI.updateServiceRequest(
        serviceRequestId,
        data,
      );
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate all service requests queries to refetch updated data
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS' ||
            query.queryKey[1] === serviceRequestId ||
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS_STATISTICS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback(resp.data.result);
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { updateServiceRequest, ...values };
};

/**
 * Custom hook to delete a TQ Connect service request
 *
 * @param onSuccessCallback - Optional callback function to execute on successful deletion
 * @returns useMutation result for deleting service request
 */
export const useDeleteTQServiceRequest = (onSuccessCallback?: () => void) => {
  const { mutate: deleteServiceRequest, ...values } = useMutation({
    mutationFn: (serviceRequestId: string) => {
      return TQConnectServiceRequestAPI.deleteServiceRequest(serviceRequestId);
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate all service requests queries to refetch updated data
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS' ||
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS_STATISTICS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { deleteServiceRequest, ...values };
};

/**
 * Custom hook to create a TQ Connect service request response
 *
 * @param serviceRequestId - The ID of the service request to create a response for
 * @param onSuccessCallback - Optional callback function to execute on successful creation
 * @returns useMutation result for creating service request response
 */
export const useCreateTQServiceRequestResponse = (
  serviceRequestId: string,
  onSuccessCallback?: (data: ITQCreateServiceRequestResponseResult) => void,
) => {
  const { mutate: createServiceRequestResponse, ...values } = useMutation({
    mutationFn: ({
      serviceRequestId,
      data,
    }: {
      serviceRequestId: string;
      data: ITQCreateServiceRequestResponseBody;
    }) => {
      return TQConnectServiceRequestAPI.createServiceRequestResponse(
        serviceRequestId,
        data,
      );
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 201 || resp?.data?.code === 200) {
        // Invalidate service request responses queries
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUEST_RESPONSES' ||
            query.queryKey[1] === serviceRequestId ||
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS_STATISTICS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback(resp.data.result);
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { createServiceRequestResponse, ...values };
};

/**
 * Custom hook to update a TQ Connect service request response (accept/reject/review)
 *
 * @param serviceRequestId - The ID of the service request to update a response for
 * @param serviceRequestResponseId - The ID of the service request response to update
 * @param onSuccessCallback - Optional callback function to execute on successful update
 * @returns useMutation result for updating service request response
 */
export const useUpdateTQServiceRequestResponse = (
  serviceRequestId: string,
  serviceRequestResponseId: string,
  onSuccessCallback?: (data: ITQUpdateServiceRequestResponseResult) => void,
) => {
  const { mutate: updateServiceRequestResponse, ...values } = useMutation({
    mutationFn: ({
      serviceRequestId,
      serviceRequestResponseId,
      data,
    }: {
      serviceRequestId: string;
      serviceRequestResponseId: string;
      data: ITQUpdateServiceRequestResponseBody;
    }) => {
      return TQConnectServiceRequestAPI.updateServiceRequestResponse(
        serviceRequestId,
        serviceRequestResponseId,
        data,
      );
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate all related queries
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUEST_RESPONSES' ||
            query.queryKey[1] === serviceRequestId ||
            query.queryKey[2] === serviceRequestResponseId ||
            query.queryKey[0] === 'TQ_CONNECT_SERVICE_REQUESTS_STATISTICS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback(resp.data.result);
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { updateServiceRequestResponse, ...values };
};

/**
 * Custom hook to create a TQ Connect tag
 *
 * @param onSuccessCallback - Optional callback function to execute on successful creation
 * @returns useMutation result for creating tag
 */
export const useCreateTQConnectTag = (
  onSuccessCallback?: (data: ITQCreateTagResult) => void,
) => {
  const { mutate: createTag, ...values } = useMutation({
    mutationFn: (data: ITQCreateTagBody) => {
      return TQConnectServiceRequestAPI.createTag(data);
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 201 || resp?.data?.code === 200) {
        // Invalidate all tags queries
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'TQ_CONNECT_TAGS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback(resp.data.result);
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { createTag, ...values };
};

/**
 * Custom hook to delete a TQ Connect tag
 *
 * @param onSuccessCallback - Optional callback function to execute on successful deletion
 * @returns useMutation result for deleting tag
 */
export const useDeleteTQConnectTag = (onSuccessCallback?: () => void) => {
  const { mutate: deleteTag, ...values } = useMutation({
    mutationFn: (tagId: string) => {
      return TQConnectServiceRequestAPI.deleteTag(tagId);
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate all tags queries
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'TQ_CONNECT_TAGS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { deleteTag, ...values };
};

/**
 * Custom hook to mark a TQ Connect activity log as read
 *
 * @param onSuccessCallback - Optional callback function to execute on successful read
 * @returns useMutation result for marking activity log as read
 */
export const useReadTQActivityLog = (onSuccessCallback?: () => void) => {
  const { mutate: markActivityLogAsRead, ...values } = useMutation({
    mutationFn: (activityId: string) => {
      return TQConnectServiceRequestAPI.markActivityLogAsRead({
        logId: activityId,
      });
    },
    onSettled: (resp, error) => {
      if (resp?.data?.code === 200) {
        // Invalidate all activity logs queries to refetch updated data
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'TQ_CONNECT_ACTIVITY_LOGS',
        });

        // Execute success callback if provided
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      } else {
        showAppError(error);
      }
    },
  });

  return { markActivityLogAsRead, ...values };
};
